import React, { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router"
import DeleteModal from "components/Common/DeleteModal"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import {
  deleteRole,
  fetchRoles,
  updateRoleStatus,
} from "store/actions/rolesAction"
import Switch from "components/Shared/Switch"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import TableTextCell from "components/Shared/TableTextCell"
import Filters from "components/Common/Filters"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DownloadCSV from "components/Shared/DownloadCSV"
import { commonItemPerPage, pageSizes } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"
import ConfirmModal from "components/Common/ConfirmModal"
import isPermissionEnabled from "common/isPermissionEnabled"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const Roles = ({ scope = "tenant", basePath = "/roles" }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Roles List ")

  const navigate = useNavigate()

  const dispatch = useDispatch()
  const {
    loading,
    isLoading,
    error,
    rolesList,
    isUpdating,
    totalCount,
    totalPages,
    currentPage,
  } = useSelector(state => state.Roles)

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const { text, type } = useSelector(state => state.Alert)
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [itemsPerPage, setItemsPerPage] = useState(commonItemPerPage)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [roleToUpdate, setRoleToUpdate] = useState(null)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const roleViewPermission =
    scope === "superadmin"
      ? { categoryId: 102, permissionId: "sa_roles_1" }
      : { categoryId: 3, permissionId: "roles_1" }
  const canViewRoles = isPermissionEnabled(
    userRole,
    roleViewPermission.categoryId,
    roleViewPermission.permissionId,
    accountType,
    accountScope
  )
  const roleCreatePermission =
    scope === "superadmin"
      ? { categoryId: 102, permissionId: "sa_roles_2" }
      : { categoryId: 3, permissionId: "roles_2" }
  const canCreateRoles = isPermissionEnabled(
    userRole,
    roleCreatePermission.categoryId,
    roleCreatePermission.permissionId,
    accountType,
    accountScope
  )
  const roleEditPermission =
    scope === "superadmin"
      ? { categoryId: 102, permissionId: "sa_roles_3" }
      : { categoryId: 3, permissionId: "roles_3" }
  const roleDeletePermission =
    scope === "superadmin"
      ? { categoryId: 102, permissionId: "sa_roles_4" }
      : { categoryId: 3, permissionId: "roles_4" }
  const canEditRoles = isPermissionEnabled(
    userRole,
    roleEditPermission.categoryId,
    roleEditPermission.permissionId,
    accountType,
    accountScope
  )
  const canDeleteRoles = isPermissionEnabled(
    userRole,
    roleDeletePermission.categoryId,
    roleDeletePermission.permissionId,
    accountType,
    accountScope
  )
  const hasRowActions = canViewRoles || canEditRoles || canDeleteRoles

  useEffect(() => {
    if (scope === "tenant" && isSuperAdminSession) {
      navigate(ROUTES.SA_ROLES)
      return
    }
    if (scope === "superadmin" && !isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (canViewRoles) {
      getRolesList()
    } else {
      navigateToAccessibleRoute(navigate)
    }
  }, [scope, isSuperAdminSession, canViewRoles, navigate])

  const getRolesList = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: 10,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(fetchRoles(payload))
    setIsApiCalled(true)
  }

  const handleEnableRole = (id, status) => {
    const payload = {
      id,
      status: status === 1 ? "0" : "1", //deactive 1 active
    }
    dispatch(updateRoleStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
    setSelectedId(id)
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // for delete
    }
    dispatch(deleteRole({ ...payload, setShowDeleteModal }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  // FILTERS
  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const fetchRolesApiCall = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    const searchParams = new URLSearchParams()
    searchTerm && searchParams.set("search", searchTerm)
    status !== 2 && searchParams.set("status", status.toString())
    sortOrder !== 1 && searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    dispatch(fetchRoles(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`${basePath}?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    fetchRolesApiCall(1)
  }

  const onPageChange = page => {
    fetchRolesApiCall(page)
  }

  const handleReset = () => {
    setItemsPerPage(commonItemPerPage)
    const payload = {
      perPage: commonItemPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(fetchRoles(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`${basePath}?page=${1}`)
        }
      })
      .catch(error => {})
    setSelectedFilters({ status: 2, sortOrder: 1 })
    setSearchTerm("")
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleExcelDownload = async () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: "ROLE",
      exportType: "xlsx",
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Roles"))
    }
  }

  const exportToCSV = async () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: "ROLE",
      exportType: "csv",
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Roles"))
    }
  }

  const confirmStatusUpdate = () => {
    if (roleToUpdate) {
      const { _id, status } = roleToUpdate
      handleEnableRole(_id, status)
      setShowStatusModal(false)
      setRoleToUpdate(null)
    }
  }
  const cancelStatusUpdate = () => {
    setShowStatusModal(false)
    setRoleToUpdate(null)
  }

  const getRolePermissionsText = role => {
    const titles = role?.permissions
      ?.filter(permission => permission.permissions.some(p => p.isEnabled))
      ?.map(permission =>
        language === "sa" ? permission.titleAr || permission.title : permission.title
      )
      ?.filter(Boolean)

    return titles?.length ? titles.join(", ") : "-"
  }

  const getRoleActionItems = role => [
    ...(canEditRoles
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`${basePath}/edit?id=${role._id}`),
          },
        ]
      : []),
    ...(canViewRoles
      ? [
          {
            key: "view",
            content: (
              <>
                <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                {t("View")}
              </>
            ),
            onClick: () =>
              navigate(`${basePath}/role-details?id=${role._id}`),
          },
        ]
      : []),
    ...(canDeleteRoles
      ? [
          {
            key: "delete",
            content: (
              <>
                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                {t("Delete")}
              </>
            ),
            onClick: () => {
              setItemIdToDelete(role._id)
              setShowDeleteModal(true)
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <div className="page-content roles_page">
        <Container fluid>
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  cardTitle={t("Roles List")}
                  isAddBtn={loading ? false : canCreateRoles}
                  addBtnText={t("Add Role")}
                  onAddBtnClick={() => navigate(`${basePath}/add`)}
                  isExportData={loading ? false : true}
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                />
                <CardBody>
                  {/* {!loading && ( */}
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize={true}
                    pageSizes={pageSizes}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    searchBy={t("Name")}
                  />
                  {/* )} */}

                  <div className="table-responsive">
                    {loading ? (
                      <TableShimmer row={7} col={7} />
                    ) : rolesList.length === 0 ? (
                      <p className="text-center my-2">{t("No roles found")}</p>
                    ) : (
                      <Table className="table table-nowrap mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th className="roles-name-cell">{t("Name")}</th>
                            <th className="roles-permissions-cell">
                              {t("Permissions")}
                            </th>
                            <th>{t("Assigned Users")}</th>
                            <th>{t("Created")}</th>
                            <th>{t("Status")}</th>
                            <th className="align-middle">{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rolesList?.map((role, index) => (
                            <tr
                              key={role._id}
                              onClick={() =>
                                canViewRoles &&
                                navigate(`${basePath}/role-details?id=${role._id}`)
                              }
                              className={canViewRoles ? "cursor-pointer" : undefined}
                            >
                              <td> {(currentPage - 1) * 10 + index + 1}</td>
                              <td className="roles-name-cell">
                                <TableTextCell
                                  text={
                                    language === "sa"
                                      ? role.nameAr || role.name
                                      : role.name
                                  }
                                />
                              </td>
                              <td className="roles-permissions-cell">
                                <TableTextCell
                                  text={getRolePermissionsText(role)}
                                  maxLines={2}
                                />
                              </td>
                              <td>{role?.users?.length}</td>
                              <td>{formatDateTime(role.createdAt) || "-"}</td>
                              <td>
                                {role.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle">
                                <div className="d-inline-flex align-items-center gap-2">
                                  {canEditRoles && (
                                    <Switch
                                      isChecked={role.status === 1}
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        setRoleToUpdate(role)
                                        setShowStatusModal(true)
                                      }}
                                      disabled={
                                        selectedId === role._id && isUpdating
                                      }
                                    />
                                  )}
                                  {hasRowActions && (
                                    <TableActionsDropdown
                                      items={getRoleActionItems(role)}
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>

                  {!loading && (
                    <div className="mt-3">
                      <Paginations
                        data={rolesList}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Role"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        onDeleteClick={confirmStatusUpdate}
        onCloseClick={cancelStatusUpdate}
        isLoading={isUpdating}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </>
  )
}

export default Roles
